import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { observer } from 'mobx-react-lite';
import { useFamilyStore, useGedcomExportStore } from '@/hooks/useStores';
import { WeTreeApiError } from '@/api/errors';
import type { GedcomExportFormat, GedcomExportMode, GedcomExportResponse } from '@/types/gedcom-export';
import { GEDCOM_EXPORT_FORMATS } from '@/types/gedcom-export';

import './export-gedcom-modal.css';

export interface ExportGedcomModalProps {
  familyId: string;
  isOpen: boolean;
  onClose: () => void;
}

type Phase = 'choose' | 'ready' | 'failed';

function errorMessage(err: unknown, fallback: string, storeFallback: string | null): string {
  if (err instanceof WeTreeApiError) return err.getUserMessage();
  return storeFallback || fallback;
}

function downloadGedcom(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function formatExportedAt(iso: string | null): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  } catch {
    return iso;
  }
}

function formatI18nKeys(value: GedcomExportFormat): {
  label: 'exportGedcom.format551Label' | 'exportGedcom.format70Label';
  hint: 'exportGedcom.format551Hint' | 'exportGedcom.format70Hint';
} {
  if (value === '5.5.1') {
    return { label: 'exportGedcom.format551Label', hint: 'exportGedcom.format551Hint' };
  }
  return { label: 'exportGedcom.format70Label', hint: 'exportGedcom.format70Hint' };
}

/**
 * Owner/admin sync GEDCOM export — full tree or delta vs last full baseline, in 5.5.1 or 7.0.
 */
const ExportGedcomModal: React.FC<ExportGedcomModalProps> = observer(({ familyId, isOpen, onClose }) => {
  const { t } = useTranslation('forms');
  const { t: tCommon } = useTranslation('common');
  const gedcomExportStore = useGedcomExportStore();
  const familyStore = useFamilyStore();
  const [phase, setPhase] = useState<Phase>('choose');
  const [mode, setMode] = useState<GedcomExportMode>('full');
  const [format, setFormat] = useState<GedcomExportFormat>('5.5.1');
  const [result, setResult] = useState<GedcomExportResponse | null>(null);
  const [phaseError, setPhaseError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPhase('choose');
    setMode('full');
    setFormat('5.5.1');
    setResult(null);
    setPhaseError(null);
    gedcomExportStore.clearError();
  }, [gedcomExportStore]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        reset();
        onClose();
      }
    },
    [onClose, reset]
  );

  useEffect(() => {
    if (!isOpen) return;
    reset();
    void gedcomExportStore.loadBaseline(familyId, '5.5.1');
  }, [isOpen, familyId, gedcomExportStore, reset]);

  const exportableCount = familyStore.exportablePeople.length;
  const hasBaseline = gedcomExportStore.hasBaselineFor(format);
  const baselineLabel = formatExportedAt(gedcomExportStore.baselineExportedAt);

  // A delta is only meaningful against a baseline in the same version, so switching version
  // reloads that version's baseline and may take the delta option away.
  const selectFormat = (next: GedcomExportFormat): void => {
    setFormat(next);
    void gedcomExportStore.loadBaseline(familyId, next).then(() => {
      if (!gedcomExportStore.hasBaselineFor(next)) setMode('full');
    });
  };

  const handleExport = async () => {
    setPhaseError(null);
    const exportResult = await gedcomExportStore.exportGedcom(familyId, mode, format);
    if (!exportResult.success) {
      setPhase('failed');
      setPhaseError(errorMessage(exportResult.error, t('exportGedcom.couldNotExport'), gedcomExportStore.lastError));
      return;
    }
    setResult(exportResult.data);
    downloadGedcom(exportResult.data.filename, exportResult.data.gedcomContent);
    setPhase('ready');
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="v01-modal-overlay v01-modal-overlay--centered">
          <Dialog.Content
            className="v01-modal v01-modal--panel export-gedcom-modal"
            aria-labelledby="export-gedcom-title"
            aria-describedby="export-gedcom-desc"
          >
            <div className="v01-modal__header v01-modal__header--compact">
              <Dialog.Title id="export-gedcom-title" className="v01-modal__title">
                {t('exportGedcom.title')}
              </Dialog.Title>
              <Dialog.Close className="v01-modal__close" aria-label={tCommon('close')}>
                ×
              </Dialog.Close>
            </div>

            <Dialog.Description id="export-gedcom-desc" className="v01-modal__desc">
              {t('exportGedcom.description')}
            </Dialog.Description>

            <div className="export-gedcom-modal__body">
              {phase === 'choose' && (
                <>
                  <p className="export-gedcom-modal__count">
                    {t('exportGedcom.personCount', { count: exportableCount })}
                  </p>

                  <RadioGroup.Root
                    className="export-gedcom-modal__modes"
                    value={mode}
                    onValueChange={(v) => setMode(v as GedcomExportMode)}
                    aria-label={t('exportGedcom.modeAria')}
                  >
                    <label className="export-gedcom-modal__mode">
                      <RadioGroup.Item value="full" className="export-gedcom-modal__radio" />
                      <span>
                        <strong>{t('exportGedcom.fullExport')}</strong>
                        <span className="export-gedcom-modal__mode-hint">{t('exportGedcom.fullExportHint')}</span>
                      </span>
                    </label>
                    <label
                      className={`export-gedcom-modal__mode${hasBaseline ? '' : ' export-gedcom-modal__mode--disabled'}`}
                    >
                      <RadioGroup.Item value="delta" className="export-gedcom-modal__radio" disabled={!hasBaseline} />
                      <span>
                        <strong>{t('exportGedcom.deltaExport')}</strong>
                        <span className="export-gedcom-modal__mode-hint">
                          {hasBaseline
                            ? t('exportGedcom.deltaExportHint', {
                                since: baselineLabel ? ` (${baselineLabel})` : ''
                              })
                            : t('exportGedcom.deltaNeedsBaseline', { format })}
                        </span>
                      </span>
                    </label>
                  </RadioGroup.Root>

                  <div className="export-gedcom-modal__section">
                    <p className="export-gedcom-modal__section-title">{t('exportGedcom.versionTitle')}</p>
                    <RadioGroup.Root
                      className="export-gedcom-modal__modes"
                      value={format}
                      onValueChange={(v) => selectFormat(v as GedcomExportFormat)}
                      aria-label={t('exportGedcom.versionAria')}
                    >
                      {GEDCOM_EXPORT_FORMATS.map((value) => {
                        const keys = formatI18nKeys(value);
                        return (
                          <label key={value} className="export-gedcom-modal__mode">
                            <RadioGroup.Item value={value} className="export-gedcom-modal__radio" />
                            <span>
                              <strong>{t(keys.label)}</strong>
                              <span className="export-gedcom-modal__mode-hint">{t(keys.hint)}</span>
                            </span>
                          </label>
                        );
                      })}
                    </RadioGroup.Root>
                  </div>

                  {phaseError ? <p className="v01-modal__error">{phaseError}</p> : null}

                  <div className="v01-modal__actions-row">
                    <Dialog.Close className="btn btn--ghost" type="button">
                      {tCommon('cancel')}
                    </Dialog.Close>
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={() => void handleExport()}
                      disabled={gedcomExportStore.exportBusy || gedcomExportStore.baselineLoading}
                    >
                      {gedcomExportStore.exportBusy ? t('actions.exporting') : t('actions.downloadGed')}
                    </button>
                  </div>
                </>
              )}

              {phase === 'ready' && result && (
                <div className="export-gedcom-modal__ready">
                  <p className="export-gedcom-modal__ready-title">{t('exportGedcom.downloadStarted')}</p>
                  <p className="export-gedcom-modal__ready-meta">
                    {t('exportGedcom.readyMeta', {
                      mode: result.mode === 'full' ? t('exportGedcom.modeFull') : t('exportGedcom.modeDelta'),
                      individuals: t('exportGedcom.individuals', { count: result.stats.changedIndividuals }),
                      families: t('exportGedcom.families', { count: result.stats.changedFamilies })
                    })}
                  </p>
                  {result.deletedXrefs.length > 0 ? (
                    <p className="export-gedcom-modal__ready-hint">
                      {t('exportGedcom.deletedHint', { count: result.deletedXrefs.length })}
                    </p>
                  ) : null}
                  <div className="v01-modal__actions-row">
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() => downloadGedcom(result.filename, result.gedcomContent)}
                    >
                      {t('actions.downloadAgain')}
                    </button>
                    <Dialog.Close className="btn btn--primary" type="button">
                      {tCommon('done')}
                    </Dialog.Close>
                  </div>
                </div>
              )}

              {phase === 'failed' && (
                <>
                  <p className="v01-modal__error">{phaseError || t('exportGedcom.exportFailed')}</p>
                  <div className="v01-modal__actions-row">
                    <Dialog.Close className="btn btn--ghost" type="button">
                      {tCommon('close')}
                    </Dialog.Close>
                    <button type="button" className="btn btn--primary" onClick={() => setPhase('choose')}>
                      {t('actions.tryAgain')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

ExportGedcomModal.displayName = 'ExportGedcomModal';

export default ExportGedcomModal;
